import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, Loader2, Sparkles, X, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankReview = () => ({
  author_name: '',
  rating: 5,
  text: '',
  location: '',
});

/**
 * Admin dialog to manage all customer reviews on a single product —
 * edit text/rating/author/location, delete, and add new manual reviews.
 *
 * Props:
 *   product      — the product object whose reviews we're managing
 *   open         — controlled open state
 *   onOpenChange — close callback
 *   onChanged    — optional callback fired after any mutation, so the parent
 *                  page can refresh review counts.
 */
export default function ReviewsDialog({ product, open, onOpenChange, onChanged }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(blankReview());
  const [isAdding, setIsAdding] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Load reviews whenever the dialog opens for a (possibly new) product
  useEffect(() => {
    if (!open || !product?.id) return;
    setLoading(true);
    setEditingId(null);
    setIsAdding(false);
    axios
      .get(`${API}/products/${product.id}/reviews`)
      .then((r) => setReviews(r.data?.reviews || []))
      .catch(() => toast.error('Could not load reviews'))
      .finally(() => setLoading(false));
  }, [open, product?.id]);

  const startEdit = (review) => {
    setIsAdding(false);
    setEditingId(review.id);
    setDraft({
      author_name: review.author_name || '',
      rating: review.rating || 5,
      text: review.text || '',
      location: review.location || '',
    });
  };

  const startAdd = () => {
    setEditingId(null);
    setIsAdding(true);
    setDraft(blankReview());
  };

  const cancelDraft = () => {
    setEditingId(null);
    setIsAdding(false);
    setDraft(blankReview());
  };

  const validate = () => {
    if (!draft.author_name.trim()) {
      toast.error('Author name is required');
      return false;
    }
    if (!draft.text.trim()) {
      toast.error('Review text is required');
      return false;
    }
    if (draft.text.length > 1000) {
      toast.error('Review text is too long (max 1000 chars)');
      return false;
    }
    return true;
  };

  const handleSaveEdit = async () => {
    if (!editingId || !validate()) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/reviews/${editingId}`, draft);
      setReviews((prev) => prev.map((r) => (r.id === editingId ? data : r)));
      toast.success('Review updated');
      cancelDraft();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update review');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/products/${product.id}/reviews`,
        draft,
      );
      setReviews((prev) => [data, ...prev]);
      toast.success('Review added');
      cancelDraft();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId) => {
    setSaving(true);
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      toast.success('Review deleted');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete review');
    } finally {
      setConfirmDeleteId(null);
      setSaving(false);
    }
  };

  const renderDraftForm = (mode) => (
    <div
      className="space-y-3 p-4 rounded-sm border border-[#D4AF37]/40 bg-[#FAF9F6]"
      data-testid="review-draft-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Author name *</Label>
          <Input
            value={draft.author_name}
            onChange={(e) =>
              setDraft({ ...draft, author_name: e.target.value })
            }
            placeholder="e.g. Sarah M."
            maxLength={80}
            data-testid="review-author"
          />
        </div>
        <div>
          <Label className="text-xs">Location (optional)</Label>
          <Input
            value={draft.location}
            onChange={(e) => setDraft({ ...draft, location: e.target.value })}
            placeholder="e.g. London, UK"
            maxLength={80}
            data-testid="review-location"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Rating</Label>
        <Select
          value={String(draft.rating)}
          onValueChange={(v) => setDraft({ ...draft, rating: Number(v) })}
        >
          <SelectTrigger className="w-32" data-testid="review-rating">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[5, 4, 3, 2, 1].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {'★'.repeat(n)}
                {'☆'.repeat(5 - n)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Review text *</Label>
        <Textarea
          value={draft.text}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          placeholder="A few sentences about the bracelet — what made it special, who it's for, how it arrived..."
          rows={4}
          maxLength={1000}
          data-testid="review-text"
        />
        <p className="text-xs text-[#84A98C] mt-1">
          {draft.text.length}/1000
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={cancelDraft}
          disabled={saving}
          data-testid="review-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={mode === 'add' ? handleAddNew : handleSaveEdit}
          disabled={saving}
          className="btn-primary"
          data-testid="review-save"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Save className="w-4 h-4 mr-1" strokeWidth={1.5} />
          )}
          {mode === 'add' ? 'Add review' : 'Save changes'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="reviews-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Reviews — {product?.name || ''}
            </DialogTitle>
            <p className="text-sm text-[#84A98C]">
              Edit, delete, or add reviews. AI-seeded reviews are marked with
              a small{' '}
              <Sparkles
                className="inline w-3 h-3 text-[#D4AF37]"
                strokeWidth={1.5}
              />
              .
            </p>
          </DialogHeader>

          <div className="space-y-4">
            {!isAdding && !editingId && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={startAdd}
                  className="btn-secondary"
                  data-testid="review-add-new"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  Add review
                </Button>
              </div>
            )}

            {isAdding && renderDraftForm('add')}

            {loading ? (
              <p className="text-center text-[#84A98C] py-8">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                Loading...
              </p>
            ) : reviews.length === 0 && !isAdding ? (
              <p className="text-center text-[#84A98C] py-8">
                No reviews yet for this product.
              </p>
            ) : (
              reviews.map((r) => (
                <div
                  key={r.id}
                  className="border border-[#CAD2C5] rounded-sm p-4"
                  data-testid={`review-row-${r.id}`}
                >
                  {editingId === r.id ? (
                    renderDraftForm('edit')
                  ) : (
                    <>
                      <div className="flex justify-between items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-[#354F52]">
                              {r.author_name}
                            </span>
                            {r.seeded && (
                              <Sparkles
                                className="w-3 h-3 text-[#D4AF37]"
                                strokeWidth={1.5}
                                aria-label="AI-seeded"
                              />
                            )}
                            <span className="text-[#D4AF37] text-sm">
                              {'★'.repeat(r.rating)}
                              <span className="text-[#CAD2C5]">
                                {'★'.repeat(5 - r.rating)}
                              </span>
                            </span>
                          </div>
                          {r.location && (
                            <p className="text-xs text-[#84A98C]">
                              {r.location}
                            </p>
                          )}
                          <p className="text-sm text-[#354F52] mt-2 whitespace-pre-wrap">
                            {r.text}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(r)}
                            className="text-[#84A98C] hover:text-[#354F52]"
                            data-testid={`review-edit-${r.id}`}
                          >
                            <Edit className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDeleteId(r.id)}
                            className="text-[#BC4749] hover:text-[#BC4749]/80"
                            data-testid={`review-delete-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the review from the product page. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
              disabled={saving}
              className="bg-[#BC4749] hover:bg-[#BC4749]/90"
              data-testid="review-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
